import type { UrlStatusUpdate } from '@kanbu/schema/contracts';
import { DocumentStatus } from '@kanbu/schema/enums';
import { useCallback, useState } from 'preact/compat';

import { AppSettings } from '@/constants/app-settings';

interface UseUrlSubmissionReturn {
  isLoading: boolean;
  organizationId: string;
  chatId: string;
  urlStatusUpdates: UrlStatusUpdate[];
  submitUrl: (url: string) => Promise<void>;
}

export function useUrlSubmission(): UseUrlSubmissionReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [chatId, setChatId] = useState('');
  const [urlStatusUpdates, setUrlStatusUpdates] = useState<UrlStatusUpdate[]>(
    [],
  );

  const submitUrl = useCallback(async (url: string) => {
    setUrlStatusUpdates([]);
    setOrganizationId('');
    setChatId('');
    setIsLoading(true);

    try {
      const response = await fetch(`${AppSettings.apiUrl}/agents/easy-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: new URL(url.startsWith('http') ? url : `https://${url}`).href,
        }),
      });

      if (!response.ok || !response.body) {
        console.error('Failed to fetch');

        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunks = decoder
          .decode(value, { stream: true })
          .split('\n')
          .filter(line => line.trim() !== '');

        for (const chunk of chunks) {
          let message:
            | UrlStatusUpdate[]
            | { chatId: string; organizationId: string };

          try {
            message = JSON.parse(chunk);
          } catch (error) {
            console.error('Error parsing JSON:', error, chunk);
            continue;
          }

          if (Array.isArray(message)) {
            setUrlStatusUpdates(prev => [
              ...prev,
              ...message.filter(
                urlStatusUpdate =>
                  prev.find(
                    prevUrlStatusUpdate =>
                      prevUrlStatusUpdate.id === urlStatusUpdate.id,
                  )?.status !== DocumentStatus.Done,
              ),
            ]);
          } else {
            setChatId(message.chatId);
            setOrganizationId(message.organizationId);
          }
        }
      }
    } catch (error) {
      console.error('Error reading stream:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    organizationId,
    chatId,
    urlStatusUpdates,
    submitUrl,
  };
}
